/**
 * AuthorizedContactCard — um contato autorizado do Bond.
 *
 * Mostra pessoa, escopos, status, validade e versão. Para contatos ATIVOS
 * oferece dois fluxos inline (espelham o padrão de revogação do B6 Guardianship):
 *   - Editar escopo/validade → ScopePicker + data, PATCH com If-Match version.
 *   - Revogar → motivo obrigatório (≥5 chars), DELETE com If-Match version.
 *
 * Cada mutação reporta erro inline (inclui conflito de versão 412 do api-client).
 */

import { useState } from 'react';

import {
  useRevokeAuthorizedContact,
  useUpdateAuthorizedContact,
} from '../../../hooks/use-bond-authorized-contacts';

import {
  buildUpdateRequest,
  editFormFrom,
  toggleScope,
  validateEditForm,
  validateRevokeReason,
  type AuthorizedContactFormState,
} from './authorized-contacts-form';
import { formatDate, scopeLabel, statusLabel } from './authorized-contacts-labels';
import { deriveMandateValidity, isScopeInert } from './scope-effectiveness';
import { ScopePicker } from './ScopePicker';

import type { BondAuthorizedContactWireItem } from '../../../lib/api-client';

type Mode = 'view' | 'edit' | 'revoke';

export function AuthorizedContactCard({
  bondId,
  contact,
}: {
  bondId: string;
  contact: BondAuthorizedContactWireItem;
}): JSX.Element {
  const [mode, setMode] = useState<Mode>('view');
  const update = useUpdateAuthorizedContact(bondId);
  const revoke = useRevokeAuthorizedContact(bondId);

  const [form, setForm] = useState<AuthorizedContactFormState>(() => editFormFrom(contact));
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);

  const isActive = contact.status === 'active';
  /**
   * **D1** — o estado que o MOTOR vê. O banco nunca escreve `'expired'` (R-B6-b: o forfeit é
   * resolvido em tempo de leitura), então o card exibia «Ativo» para mandato vencido. Derivado
   * pela função do próprio caminho de autorização — nunca por uma 2ª regra de data no cliente.
   * As ações (editar/revogar) seguem a coluna `status`: um mandato caducado ainda pode ter a
   * validade RENOVADA, que é justamente o conserto que o operador precisa enxergar.
   */
  const validity = deriveMandateValidity(contact);

  function resetTo(next: Mode): void {
    setError(null);
    setReason('');
    setForm(editFormFrom(contact));
    setMode(next);
  }

  function handleSaveEdit(): void {
    const v = validateEditForm(form);
    if (v !== null) {
      setError(v);
      return;
    }
    setError(null);
    update.mutate(
      { contactId: contact.id, version: contact.version, body: buildUpdateRequest(form) },
      {
        onSuccess: () => setMode('view'),
        onError: (err) => setError(err.message !== '' ? err.message : 'Falha ao salvar.'),
      },
    );
  }

  function handleConfirmRevoke(): void {
    const v = validateRevokeReason(reason);
    if (v !== null) {
      setError(v);
      return;
    }
    setError(null);
    revoke.mutate(
      {
        contactId: contact.id,
        version: contact.version,
        body: { revocationReason: reason.trim() },
      },
      {
        onSuccess: () => setMode('view'),
        onError: (err) => setError(err.message !== '' ? err.message : 'Falha ao revogar.'),
      },
    );
  }

  return (
    <div className={`bac-card bac-card--${validity}`}>
      <div className="bac-card__head">
        <span className="bac-card__person" title={contact.personId}>
          {contact.personId}
        </span>
        <span
          className={`bac-status bac-status--${validity}`}
          title={
            validity === 'expired'
              ? 'A validade do mandato decorreu — os escopos deixaram de valer no gate. Editar a validade renova a autorização.'
              : undefined
          }
        >
          {statusLabel(validity)}
        </span>
      </div>

      <div className="bac-card__scopes">
        {contact.scopes.length === 0 ? (
          <span className="bac-muted">Sem escopos</span>
        ) : (
          contact.scopes.map((s) =>
            isScopeInert(s) ? (
              <span
                key={s}
                className="bac-tag bac-tag--inert"
                title="Nenhuma ação da plataforma exige esta chave hoje — este escopo não muda o que o contato pode fazer."
              >
                {scopeLabel(s)} <span className="bac-tag__inert">sem efeito</span>
              </span>
            ) : (
              <span key={s} className="bac-tag">
                {scopeLabel(s)}
              </span>
            ),
          )
        )}
      </div>

      <dl className="bac-card__meta">
        <div>
          <dt>Validade</dt>
          <dd>{contact.expiresAt !== null ? formatDate(contact.expiresAt) : 'Sem expiração'}</dd>
        </div>
        <div>
          <dt>Autorizado em</dt>
          <dd>{formatDate(contact.createdAt)}</dd>
        </div>
        {contact.status === 'revoked' && (
          <div>
            <dt>Revogado</dt>
            <dd>
              {formatDate(contact.revokedAt)} · {contact.revocationReason ?? '—'}
            </dd>
          </div>
        )}
      </dl>

      {/* Ações (apenas contatos ativos) */}
      {isActive && mode === 'view' && (
        <div className="bac-card__actions">
          <button type="button" className="bac-btn bac-btn--ghost" onClick={() => resetTo('edit')}>
            Editar escopo / validade
          </button>
          <button
            type="button"
            className="bac-btn bac-btn--danger"
            onClick={() => resetTo('revoke')}
          >
            Revogar
          </button>
        </div>
      )}

      {/* Editar inline */}
      {mode === 'edit' && (
        <div className="bac-inline">
          <ScopePicker
            selected={form.scopes}
            onToggle={(s) => setForm((p) => ({ ...p, scopes: toggleScope(p.scopes, s) }))}
            idPrefix={`edit-${contact.id}`}
          />
          <label className="bac-field">
            <span className="bac-field__label">Validade (opcional)</span>
            <input
              type="date"
              className="bac-input"
              value={form.expiresAt}
              onChange={(e) => setForm((p) => ({ ...p, expiresAt: e.target.value }))}
            />
          </label>
          {error !== null && (
            <p className="bac-alert bac-alert--error" role="alert">
              {error}
            </p>
          )}
          <div className="bac-inline__actions">
            <button
              type="button"
              className="bac-btn bac-btn--ghost"
              onClick={() => setMode('view')}
            >
              Cancelar
            </button>
            <button
              type="button"
              className="bac-btn bac-btn--primary"
              disabled={update.isPending}
              onClick={handleSaveEdit}
            >
              {update.isPending ? 'Salvando…' : 'Salvar alterações'}
            </button>
          </div>
        </div>
      )}

      {/* Revogar inline */}
      {mode === 'revoke' && (
        <div className="bac-inline">
          <label className="bac-field">
            <span className="bac-field__label">Motivo da revogação</span>
            <textarea
              className="bac-textarea"
              rows={2}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="ex: a pedido do titular / autorização cessada"
            />
          </label>
          {error !== null && (
            <p className="bac-alert bac-alert--error" role="alert">
              {error}
            </p>
          )}
          <div className="bac-inline__actions">
            <button
              type="button"
              className="bac-btn bac-btn--ghost"
              onClick={() => setMode('view')}
            >
              Cancelar
            </button>
            <button
              type="button"
              className="bac-btn bac-btn--danger"
              disabled={revoke.isPending}
              onClick={handleConfirmRevoke}
            >
              {revoke.isPending ? 'Revogando…' : 'Confirmar revogação'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
